import * as React from "react";
import {observer, inject} from "mobx-react";
import type {Boundary} from "../../../../../../data/models/Boundary";
import type {Xyicon} from "../../../../../../data/models/Xyicon";
import {XyiconFeature} from "../../../../../../generated/api/base";
import {ToggleContainer} from "../../../../../widgets/container/ToggleContainer";
import type {AppState} from "../../../../../../data/state/AppState";
import type {IModel} from "../../../../../../data/models/Model";
import {SpaceItemContainer} from "../../../../space/spaceeditor/ui/toolbar/SpaceItemContainer";
import type {IXyiconLinkObject} from "../../../../../../data/state/AppActions";
import {CrossPortfolioXyicon} from "../../../../space/spaceeditor/ui/actionbar/CrossPortfolioXyicon";
import type {ICrossPortfolioLinkData} from "../../../../space/spaceeditor/ui/actionbar/CrossPortfolioXyicon";
import type {Link} from "../../../../../../data/models/Link";
import {LinkBreakers} from "../../../../space/spaceeditor/ui/actionbar/LinkBreakers";
import {Functions} from "../../../../../../utils/function/Functions";

interface IDetailsSectionProps {
	readonly item: IModel;
	readonly feature: XyiconFeature;
	readonly appState?: AppState;
	readonly saveStateToLocalStorage?: boolean;
}

@inject("appState")
@observer
export class LinksSection extends React.Component<IDetailsSectionProps> {
	private xyiconLinks: Link[] = [];

	private renderLinks(feature: XyiconFeature) {
		const {appState, item} = this.props;
		const {actions} = appState;

		const itemId = item.id;

		const boundaries: Boundary[] = [...actions.getLinksXyiconBoundary(itemId), ...actions.getLinksBoundaryBoundary(itemId, true)].map(
			(value) => value.object,
		);
		const xyiconsForBoundary: IXyiconLinkObject[] = actions.getLinksXyiconsForBoundary(itemId);
		const xyiconsForXyicon: IXyiconLinkObject[] = actions.getLinksXyiconXyicon(itemId);
		const crossPortfolioXyiconLinks = actions.getCrossPortfolioLinksXyiconXyicon(itemId);
		const xyicons: (Xyicon | ICrossPortfolioLinkData)[] = [...xyiconsForBoundary.map((obj) => obj.object)];
		const embeddedXyicons: Xyicon[] = [];
		const xyiconsLinkedViaPort: (Xyicon | ICrossPortfolioLinkData)[] = []; // xyicons that are connected to the selected xyicon via their ports, BUT not connected to the selected xyicon's port, but to the selected xyicon itself

		const filteredXyiconLinks = xyiconsForXyicon
			.filter(({link}) => {
				const isSelectedTypeFrom = link.objectAID === itemId;
				return (isSelectedTypeFrom && !link.isEmbedded) || !isSelectedTypeFrom;
			})
			.map(({link}) => link);

		const additionalXyiconLinks = crossPortfolioXyiconLinks.map(({link}) => link);

		this.xyiconLinks = [...filteredXyiconLinks, ...additionalXyiconLinks];

		for (const obj of xyiconsForXyicon) {
			const {link, object} = obj;
			const isSelectedTypeFrom = link.objectAID === itemId;

			if (isSelectedTypeFrom) {
				if (link.isEmbedded) {
					// boundary/xyicon is connected to a xyicon
					embeddedXyicons.push(object);
				} else if (link.objectAPortID) {
					// xyicon's port is connected to another xyicon, or another xyicon's port (we handle this case in the "ports" section)
				} else if (link.objectBPortID) {
					// xyicon is connected to a xyicon's port
					xyiconsLinkedViaPort.push(object);
				} // boundary/xyicon is connected to a xyicon
				else {
					xyicons.push(object);
				}
			} else {
				if (link.objectBPortID) {
				} else if (link.objectAPortID) {
					// xyicon is connected to another xyicon's port
					xyiconsLinkedViaPort.push(object);
				} // xyicon is connected to a xyicon
				else {
					xyicons.push(object);
				}
			}
		}

		for (const link of crossPortfolioXyiconLinks) {
			if (!link.onePortId) {
				if (link.otherPortId) {
					xyiconsLinkedViaPort.push(link);
				} // if (!link.otherPortId)
				else {
					xyicons.push(link);
				}
			}
		}

		return this.renderLinkContainers(feature, boundaries, xyicons, embeddedXyicons, xyiconsLinkedViaPort);
	}

	private getLinkElements(items: (Boundary | Xyicon | ICrossPortfolioLinkData)[], spaceItemType?: "linked-xyicon" | null) {
		return (
			<div className="linkElements">
				{items.map((item: Xyicon | Boundary | ICrossPortfolioLinkData) => {
					const spaceItem = item as Xyicon | Boundary;
					const crossPortfolioLinkData = item as ICrossPortfolioLinkData;
					const index = items.findIndex((item) => item === spaceItem);

					if (!(item as Xyicon).ownFeature) {
						return (
							<CrossPortfolioXyicon
								key={crossPortfolioLinkData.link.id}
								transport={this.props.appState.app.transport}
								linkData={crossPortfolioLinkData}
								showBreakLinkButton={true}
								showDeleteButton={false}
								isOption={false}
								portfolioId={this.props.item.portfolioId}
							/>
						);
					} else {
						return (
							<SpaceItemContainer
								key={spaceItem.id}
								item={spaceItem}
								showInfoButton={true}
								showDeleteButton={false}
								onBreakLinkClick={
									spaceItemType === "linked-xyicon" && this.props.item.ownFeature === XyiconFeature.Xyicon
										? () =>
												LinkBreakers.breakLinks(this.props.appState.app.transport, [this.xyiconLinks[index].id], false, this.props.item.portfolioId)
										: Functions.emptyFunction
								}
							/>
						);
					}
				})}
			</div>
		);
	}

	private renderLinkContainers(
		feature: XyiconFeature,
		boundaries: Boundary[],
		xyicons: (Xyicon | ICrossPortfolioLinkData)[],
		embeddedXyicons: Xyicon[],
		xyiconsLinkedViaPort: (Xyicon | ICrossPortfolioLinkData)[],
	) {
		const arrayOfContainers = [
			<ToggleContainer
				key={0}
				title="Boundaries"
				saveStateToLocalStorage={this.props.saveStateToLocalStorage}
				className="FieldSection"
			>
				{this.getLinkElements(boundaries)}
			</ToggleContainer>,
			<ToggleContainer
				key={1}
				title="Xyicons"
				saveStateToLocalStorage={this.props.saveStateToLocalStorage}
				className="FieldSection"
			>
				{this.getLinkElements(xyicons, "linked-xyicon")}
			</ToggleContainer>,
		];

		if (feature === XyiconFeature.Xyicon) {
			arrayOfContainers.push(
				<ToggleContainer
					key={2}
					title="Embedded Xyicons"
					saveStateToLocalStorage={this.props.saveStateToLocalStorage}
					className="FieldSection"
				>
					{this.getLinkElements(embeddedXyicons)}
				</ToggleContainer>,
				<ToggleContainer
					key={3}
					title="Ports"
					saveStateToLocalStorage={this.props.saveStateToLocalStorage}
					className="FieldSection"
				>
					{this.getLinkElements(xyiconsLinkedViaPort, "linked-xyicon")}
				</ToggleContainer>,
			);
		}
		return arrayOfContainers;
	}

	public override render() {
		const {feature} = this.props;

		return (
			(feature === XyiconFeature.Xyicon || feature === XyiconFeature.Boundary) && (
				<ToggleContainer
					title="Links"
					saveStateToLocalStorage={this.props.saveStateToLocalStorage}
				>
					{this.renderLinks(this.props.feature)}
				</ToggleContainer>
			)
		);
	}
}
